/**
 * DesignDayPreview Component
 *
 * Modal showing detailed design day information and full IDF object.
 */

import { useState } from 'react';
import type { ParsedDesignDay } from '../../types';

export interface DesignDayPreviewProps {
  designDay: ParsedDesignDay;
  onClose: () => void;
}

type PreviewTab = 'details' | 'idf';

export function DesignDayPreview({ designDay, onClose }: DesignDayPreviewProps) {
  const [activeTab, setActiveTab] = useState<PreviewTab>('details');
  const [copied, setCopied] = useState(false);

  const handleCopyIdf = async () => {
    try {
      await navigator.clipboard.writeText(designDay.idfObject);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = designDay.idfObject;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h2 className="text-lg font-semibold">{designDay.name}</h2>
            <p className="text-sm text-muted-foreground">
              SizingPeriod:DesignDay
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
            aria-label="Close preview"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {(['details', 'idf'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'details' && 'Field Details'}
              {tab === 'idf' && 'IDF Object'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="text-sm text-muted-foreground">Type</div>
                  <div className="font-medium capitalize">{designDay.type}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="text-sm text-muted-foreground">Day Type</div>
                  <div className="font-medium">{designDay.dayType}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="text-sm text-muted-foreground">Date</div>
                  <div className="font-medium">
                    {getMonthName(designDay.month)} {designDay.dayOfMonth}
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="text-sm text-muted-foreground">Dry-Bulb Temp</div>
                  <div className="font-medium">{designDay.dryBulbTemp.toFixed(1)}°C</div>
                </div>
                {designDay.wetBulbTemp !== undefined && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <div className="text-sm text-muted-foreground">Wet-Bulb Temp</div>
                    <div className="font-medium">{designDay.wetBulbTemp.toFixed(1)}°C</div>
                  </div>
                )}
                {designDay.windSpeed !== undefined && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <div className="text-sm text-muted-foreground">Wind Speed</div>
                    <div className="font-medium">{designDay.windSpeed.toFixed(1)} m/s</div>
                  </div>
                )}
              </div>

              {/* All fields table */}
              <div>
                <h3 className="mb-2 text-sm font-medium uppercase text-muted-foreground">
                  All Fields
                </h3>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">#</th>
                        <th className="px-3 py-2 text-left font-medium">Field Name</th>
                        <th className="px-3 py-2 text-left font-medium">Value</th>
                        <th className="px-3 py-2 text-left font-medium">Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {designDay.fields.map((field, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2 text-muted-foreground">{index + 1}</td>
                          <td className="px-3 py-2">{field.name}</td>
                          <td className="px-3 py-2 font-mono">
                            {field.value || <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {field.units || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'idf' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  IDF object ready for injection into your model
                </p>
                <button
                  onClick={handleCopyIdf}
                  className="flex items-center gap-1 rounded border px-2 py-1 text-sm hover:bg-muted"
                >
                  {copied ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-600"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <pre className="overflow-x-auto rounded-lg bg-muted p-4 text-sm font-mono whitespace-pre-wrap">
                {designDay.idfObject}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          <button
            onClick={onClose}
            className="rounded border px-4 py-2 text-sm hover:bg-muted"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Get month name from number.
 */
function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1] ?? 'Unknown';
}
